/**
 * AudioWorklet playback: a jitter buffer that turns bursty network frames into the
 * steady 128-sample render quanta the audio graph asks for.
 *
 * This file is loaded into an AudioWorkletGlobalScope, which cannot resolve module
 * imports — so it deliberately has no runtime dependencies. The only import is a
 * type import, which is erased. `JitterBuffer` is exported so the buffering rules
 * can be tested in Node without an audio graph.
 *
 * No SharedArrayBuffer: that would force COOP/COEP headers on the whole site. The
 * main thread transfers Float32Array buffers through `port.postMessage` instead, so
 * frames still cross the thread boundary without being copied.
 */

import type { WorkletCommand, WorkletStats } from './messages';

declare const sampleRate: number;
declare class AudioWorkletProcessor {
  readonly port: MessagePort;
  constructor();
}
declare function registerProcessor(
  name: string,
  processorCtor: new () => AudioWorkletProcessor,
): void;

export const PLAYBACK_PROCESSOR = 'auxcast-playback';

export type BufferState = 'filling' | 'playing';

export class JitterBuffer {
  private data: Float32Array[] = [];
  private capacity = 0;
  private channels = 0;
  private readIndex = 0;
  private available = 0;
  private target = 0;
  private cap = 0;
  private bufferState: BufferState = 'filling';
  private underrunCount = 0;
  private discarded = 0;

  constructor(channels: number, capacitySamples: number, targetSamples = 0, capSamples = 0) {
    this.configure(channels, capacitySamples, targetSamples, capSamples);
  }

  configure(
    channels: number,
    capacitySamples: number,
    targetSamples: number,
    capSamples: number,
  ): void {
    const nextChannels = Math.max(1, Math.trunc(channels));
    const nextCapacity = Math.max(128, Math.trunc(capacitySamples));
    if (nextChannels !== this.channels || nextCapacity !== this.capacity) {
      this.channels = nextChannels;
      this.capacity = nextCapacity;
      this.data = [];
      for (let c = 0; c < nextChannels; c++) this.data.push(new Float32Array(nextCapacity));
    }
    this.clear();
    this.tune(targetSamples, capSamples);
  }

  /**
   * Retune live. The buffer snaps to the new target immediately — trimming what
   * is ahead of it, or dropping back to pre-buffering to build it up — so the
   * latency slider changes the latency the listener actually hears instead of only
   * changing what happens after the next glitch. Building a deeper buffer costs a
   * short silence; there is no way to add latency without one.
   */
  tune(targetSamples: number, capSamples: number): void {
    this.target = Math.max(0, Math.min(this.capacity, Math.trunc(targetSamples)));
    this.cap = Math.max(this.target, Math.min(this.capacity, Math.trunc(capSamples)));

    if (this.available > this.target) this.discard(this.available - this.target);
    else if (this.available < this.target) this.bufferState = 'filling';
  }

  get bufferedSamples(): number {
    return this.available;
  }

  get underruns(): number {
    return this.underrunCount;
  }

  get discardedSamples(): number {
    return this.discarded;
  }

  get state(): BufferState {
    return this.bufferState;
  }

  get targetSamples(): number {
    return this.target;
  }

  get capSamples(): number {
    return this.cap;
  }

  clear(): void {
    this.readIndex = 0;
    this.available = 0;
    this.bufferState = 'filling';
  }

  resetCounters(): void {
    this.underrunCount = 0;
    this.discarded = 0;
  }

  /**
   * Append one decoded frame. Live audio is worthless late, so an overrun discards
   * the OLDEST samples — the listener hears a small skip and stays current instead
   * of drifting further behind for the rest of the session.
   */
  push(planes: readonly Float32Array[]): void {
    const frames = planes[0]?.length ?? 0;
    if (frames === 0 || this.channels === 0) return;

    if (frames >= this.cap) {
      // A single frame larger than the whole ceiling: keep only its tail.
      this.clear();
      const keep = Math.min(this.cap, this.capacity);
      this.discarded += frames - keep;
      this.writeInto(planes, frames - keep, keep);
      return;
    }

    const overflow = this.available + frames - this.cap;
    if (overflow > 0) this.discard(overflow);
    this.writeInto(planes, 0, frames);
  }

  /**
   * Fill `output` with `frames` samples per channel. Output channels beyond what
   * the stream carries repeat the last one, so a mono sender feeds both ears.
   */
  pull(output: Float32Array[], frames: number): void {
    if (frames <= 0) return;

    if (this.bufferState === 'filling') {
      const needed = Math.max(this.target, frames);
      if (this.available < needed) {
        this.silence(output, 0, frames);
        return;
      }
      this.bufferState = 'playing';
      // Start playing at exactly the target latency. Nothing is consumed while
      // pre-buffering, so a graph that takes 100 ms to start rendering would
      // otherwise bank that delay and carry it for the rest of the session — the
      // latency slider would stop meaning anything.
      if (this.available > needed) this.discard(this.available - needed);
    }

    const take = Math.min(frames, this.available);
    if (take > 0) this.readInto(output, 0, take);
    if (take < frames) {
      this.silence(output, take, frames - take);
      this.underrunCount += 1;
      this.bufferState = 'filling';
    }
  }

  private discard(samples: number): void {
    const n = Math.min(samples, this.available);
    if (n <= 0) return;
    this.readIndex = (this.readIndex + n) % this.capacity;
    this.available -= n;
    this.discarded += n;
  }

  private writeInto(planes: readonly Float32Array[], sourceOffset: number, frames: number): void {
    const writeIndex = (this.readIndex + this.available) % this.capacity;
    const first = Math.min(frames, this.capacity - writeIndex);

    for (let c = 0; c < this.channels; c++) {
      const target = this.data[c];
      const source = planes[Math.min(c, planes.length - 1)];
      if (!target || !source) continue;
      target.set(source.subarray(sourceOffset, sourceOffset + first), writeIndex);
      if (frames > first) {
        target.set(source.subarray(sourceOffset + first, sourceOffset + frames), 0);
      }
    }
    this.available += frames;
  }

  private readInto(output: Float32Array[], outputOffset: number, frames: number): void {
    const first = Math.min(frames, this.capacity - this.readIndex);

    for (let c = 0; c < output.length; c++) {
      const target = output[c];
      const source = this.data[Math.min(c, this.channels - 1)];
      if (!target || !source) continue;
      target.set(source.subarray(this.readIndex, this.readIndex + first), outputOffset);
      if (frames > first) target.set(source.subarray(0, frames - first), outputOffset + first);
    }
    this.readIndex = (this.readIndex + frames) % this.capacity;
    this.available -= frames;
  }

  private silence(output: Float32Array[], offset: number, frames: number): void {
    for (const channel of output) channel.fill(0, offset, offset + frames);
  }
}

/** Render quanta between stats messages: ~85 ms at 48 kHz. */
const STATS_INTERVAL_QUANTA = 32;

if (typeof registerProcessor === 'function' && typeof AudioWorkletProcessor !== 'undefined') {
  class PlaybackProcessor extends AudioWorkletProcessor {
    private readonly buffer: JitterBuffer;
    private quantaSinceStats = 0;

    constructor() {
      super();
      const fallbackCapacity = Math.ceil(sampleRate * 0.9);
      this.buffer = new JitterBuffer(2, fallbackCapacity, 0, fallbackCapacity);
      this.port.onmessage = (event: MessageEvent<WorkletCommand>) => {
        this.handle(event.data);
      };
    }

    private handle(command: WorkletCommand): void {
      switch (command.type) {
        case 'format':
          this.buffer.configure(
            command.channels,
            command.capacitySamples,
            command.targetSamples,
            command.capSamples,
          );
          this.buffer.resetCounters();
          return;
        case 'tune':
          this.buffer.tune(command.targetSamples, command.capSamples);
          return;
        case 'frames':
          this.buffer.push(command.planes);
          return;
        case 'reset':
          this.buffer.clear();
          return;
        default:
          return;
      }
    }

    process(_inputs: Float32Array[][], outputs: Float32Array[][]): boolean {
      const output = outputs[0];
      if (!output || output.length === 0) return true;

      this.buffer.pull(output, output[0]?.length ?? 0);

      if (++this.quantaSinceStats >= STATS_INTERVAL_QUANTA) {
        this.quantaSinceStats = 0;
        const stats: WorkletStats = {
          type: 'stats',
          bufferedSamples: this.buffer.bufferedSamples,
          underruns: this.buffer.underruns,
          discardedSamples: this.buffer.discardedSamples,
          state: this.buffer.state,
        };
        this.port.postMessage(stats);
      }
      return true;
    }
  }

  registerProcessor(PLAYBACK_PROCESSOR, PlaybackProcessor);
}
